import React, { useState, useEffect } from 'react';
import { addDoc, collection, doc, updateDoc, writeBatch, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { format } from 'date-fns';
import { zonedTimeToUtc, utcToZonedTime } from 'date-fns-tz';
import type { TimeSlot, StarAvailabilitySettings } from '../types/availability';
import { generateRecurringSlots } from '../util/slotUtils';
import TimeSlotPicker from './TimeSlotPicker';

interface AddSlotModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  settings: StarAvailabilitySettings;
  onSlotAdded: () => void;
  initialDate: Date;
  editSlot?: TimeSlot | null;
}

const AddSlotModal: React.FC<AddSlotModalProps> = ({
  isOpen,
  onClose,
  userId,
  settings,
  onSlotAdded,
  initialDate,
  editSlot,
}) => {
  const [date, setDate] = useState(format(initialDate, 'yyyy-MM-dd'));
  const [startTime, setStartTime] = useState(format(initialDate, 'HH:mm'));
  const [duration, setDuration] = useState(settings.minCallDuration.toString());
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringPattern, setRecurringPattern] = useState<'daily' | 'weekly'>('weekly');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userTimezone] = useState<string>(Intl.DateTimeFormat().resolvedOptions().timeZone);

  // Click outside to close modal
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // Check if click is on the backdrop (not the modal content)
      if (target.classList.contains('slot-modal-backdrop')) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    // Clear previous error when modal opens or slot changes
    setError(null);

    if (editSlot) {
      // Convert UTC times from database to user's timezone for display
      const slotInUserTz = utcToZonedTime(editSlot.startTime, userTimezone);
      setDate(format(slotInUserTz, 'yyyy-MM-dd'));
      setStartTime(format(slotInUserTz, 'HH:mm'));
      setDuration(editSlot.duration.toString());
      setIsRecurring(editSlot.isRecurring);
      if (editSlot.recurringPattern) {
        setRecurringPattern(editSlot.recurringPattern);
      }
    } else {
      // Convert initial date to user's timezone for display
      const initialInUserTz = utcToZonedTime(initialDate, userTimezone);
      setDate(format(initialInUserTz, 'yyyy-MM-dd'));
      setStartTime(format(initialInUserTz, 'HH:mm'));
      setDuration(settings.minCallDuration.toString());
      setIsRecurring(false);
      setRecurringPattern('weekly');
    }
  }, [editSlot, initialDate, settings, userTimezone]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
  
    try {
      // Validate duration
      const durationMinutes = parseInt(duration);
      if (durationMinutes > 1440) {
        setError('Slot duration cannot exceed 24 hours (1440 minutes)');
        setLoading(false);
        return;
      }
      if (durationMinutes < settings.minCallDuration) {
        setError(`Slot duration must be at least ${settings.minCallDuration} minutes`);
        setLoading(false);
        return;
      }

      // Create date in user's timezone first
      const dateTimeInUserTz = new Date(`${date}T${startTime}`);
      
      // Convert to UTC for storage
      const startDateTimeUTC = zonedTimeToUtc(dateTimeInUserTz, userTimezone);
      const endDateTimeUTC = new Date(
        startDateTimeUTC.getTime() + parseInt(duration) * 60000,
      );
  
      // Check for overlapping slots
      const overlappingQuery = query(
        collection(db, 'slots'),
        where('starId', '==', userId),
        where('startTime', '<', endDateTimeUTC),
        where('endTime', '>', startDateTimeUTC)
      );

      const overlappingSlots = await getDocs(overlappingQuery);
      const hasOverlap = overlappingSlots.docs.some(doc => {
        // Exclude the current slot if we're editing
        if (editSlot && doc.id === editSlot.id) {
          return false;
        }
        
        const slot = doc.data();
        const slotStart = slot.startTime.toDate();
        const slotEnd = slot.endTime.toDate();
        
        // Check if slots actually overlap
        return (
          (startDateTimeUTC >= slotStart && startDateTimeUTC < slotEnd) ||
          (endDateTimeUTC > slotStart && endDateTimeUTC <= slotEnd) ||
          (startDateTimeUTC <= slotStart && endDateTimeUTC >= slotEnd)
        );
      });

      if (hasOverlap) {
        setError('This time slot overlaps with an existing slot');
        setLoading(false);
        return;
      }

      const slotData: any = {
        starId: userId,
        startTime: startDateTimeUTC,
        endTime: endDateTimeUTC,
        duration: parseInt(duration),
        isRecurring,
        recurringPattern: isRecurring ? recurringPattern : null,
        basePrice: parseInt(duration) * settings.pricePerMinute,
        currentBid: 0,
        bidHistory: [],
        bookings: [],
      };


      if (editSlot) {
        // Preserve existing bookings when editing
        const existingBookings = editSlot.bookings || [];

        // Validate that new time range covers all existing bookings
        if (existingBookings.length > 0) {
          const bookingsOutside = existingBookings.some(booking => {
            const bStart = booking.startTime instanceof Date ? booking.startTime : new Date(booking.startTime);
            const bEnd = booking.endTime instanceof Date ? booking.endTime : new Date(booking.endTime);
            return bStart < startDateTimeUTC || bEnd > endDateTimeUTC;
          });

          if (bookingsOutside) {
            setError('Cannot change slot time: existing bookings fall outside the new time range. Cancel those bookings first.');
            setLoading(false);
            return;
          }
        }

        // Keep existing bookings, bidHistory, and currentBid
        delete slotData.bookings;
        delete slotData.bidHistory;
        delete slotData.currentBid;
        await updateDoc(doc(db, 'slots', editSlot.id), slotData);
      } else if (isRecurring) {
        // For recurring slots, we need to check each slot for overlaps
        const recurringSlots = generateRecurringSlots(slotData, recurringPattern);
        
        // Check each recurring slot for overlaps
        let hasRecurringOverlap = false;
        for (const slot of recurringSlots) {
          const recurringOverlapQuery = query(
            collection(db, 'slots'),
            where('starId', '==', userId),
            where('startTime', '<', slot.endTime),
            where('endTime', '>', slot.startTime)
          );
          
          const recurringOverlaps = await getDocs(recurringOverlapQuery);
          if (recurringOverlaps.docs.length > 0) {
            hasRecurringOverlap = true;
            break;
          }
        }
  
        if (hasRecurringOverlap) {
          setError('Some recurring slots overlap with existing slots');
          setLoading(false);
          return;
        }
  
        // If no overlaps, create all recurring slots
        const batch = writeBatch(db);
        recurringSlots.forEach((slot: any) => {
          const newSlotRef = doc(collection(db, 'slots'));
          batch.set(newSlotRef, slot);
        });
  
        await batch.commit();
      } else {
        await addDoc(collection(db, 'slots'), slotData);
        console.log('Slot created successfully', slotData);
      }
  
      onSlotAdded();
      onClose();
    } catch (err) {
      setError('Failed to create slot: ' + (err instanceof Error ? err.message : String(err)));
      console.error('Error creating slot:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="slot-modal-backdrop fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full shadow-lg">
        <h2 className="text-xl font-semibold mb-4 text-gray-900">
          {editSlot ? 'Edit Slot' : 'Add New Slot'}
        </h2>

        {/* <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-700">
            <strong>Timezone:</strong> Times are displayed in your local timezone ({userTimezone}) 
            but will be saved as UTC for global consistency.
          </p>
        </div> */}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date
            </label>
            <input
              type="date"
              min={format(new Date(), 'yyyy-MM-dd')}
              max={format(
                new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
                'yyyy-MM-dd',
              )}
              value={date}
              onChange={e => setDate(e.target.value)}
              required
              className="w-full appearance-none bg-white border border-gray-300 rounded-lg px-3 py-2 text-left text-gray-900 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Time
            </label>
            <TimeSlotPicker
              value={startTime}
              onChange={setStartTime}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Duration (minutes)
            </label>
            <input
              type="number"
              min={settings.minCallDuration}
              max={1440}
              value={duration}
              onChange={e => setDuration(e.target.value)}
              required
              className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-gray-900 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            />
            <p className="text-xs text-gray-500 mt-1">Maximum: 1440 minutes (24 hours)</p>
          </div>

          {!editSlot && (
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isRecurring"
                checked={isRecurring}
                onChange={e => setIsRecurring(e.target.checked)}
                className="rounded border-gray-300 bg-white text-purple-600 focus:ring-purple-500"
              />
              <label
                htmlFor="isRecurring"
                className="text-sm font-medium text-gray-700">
                Make this a recurring slot
              </label>
            </div>
          )}

          {isRecurring && !editSlot && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Repeat Pattern
              </label>
              <select
                value={recurringPattern}
                onChange={e =>
                  setRecurringPattern(e.target.value as 'daily' | 'weekly')
                }
                className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-gray-900 focus:ring-2 focus:ring-purple-500 focus:border-purple-500">
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
              </select>
            </div>
          )}

          {error && <p className="text-red-600 text-sm">{error}</p>}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-md font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500">
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-4 py-2 rounded-md font-medium disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500">
              {loading ? 'Saving...' : editSlot ? 'Save Changes' : 'Create Slot'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddSlotModal;